import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {FormPhoneField} from '@common/ui/forms/input-field/phone-field/phone-field';
import {FormTextField} from '../../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../../ui/buttons/button';
import {Form} from '../../../ui/forms/form';
import {Trans} from '../../../i18n/trans';
import {useBootstrapData} from '../../../core/bootstrap-data/bootstrap-data-context';
import {onFormQueryError} from '../../../errors/on-form-query-error';
import {useNavigate} from '../../../utils/hooks/use-navigate';
import {useLocation} from 'react-router-dom';
import {
  getAuthPagePathWithRedirectUri,
  getAuthRedirectFromSearch,
} from '../../auth-redirects';
import {getTrackingParametersFromCookie} from '../../../utils/hooks/use-tracking-parameters';
import {useHandleLoginSuccess} from '../../requests/use-login';
import {
  useStartPhoneSignup,
  useVerifyPhoneSignup,
} from '../../requests/phone-otp-auth';
import {OtpCodeField} from './otp-code-field';

interface CredentialsStep {
  phone: string;
  password: string;
  password_confirmation: string;
}
interface OtpStep {
  code: string;
}

/**
 * Phone signup for the password + OTP mode: choose a phone and password, verify
 * a one-time code, and the account is created and signed in.
 */
export function PhoneSignupForm() {
  const {data: {environment}} = useBootstrapData();
  const handleSuccess = useHandleLoginSuccess();
  const navigate = useNavigate();
  const location = useLocation();

  const [creds, setCreds] = useState<CredentialsStep | null>(null);

  const credentialsForm = useForm<CredentialsStep>();
  const otpForm = useForm<OtpStep>();
  const start = useStartPhoneSignup();
  const verify = useVerifyPhoneSignup();

  // if the number is already registered, send the user to sign in with the
  // number carried over, rather than failing on the signup form
  const handleError = (r: any, form: typeof credentialsForm | typeof otpForm, phone: string) => {
    if (r?.response?.data?.account_exists) {
      // keep ?next= (e.g. a trial link) across the hop to the login page
      const next = getAuthRedirectFromSearch(location.search);
      navigate(
        next ? getAuthPagePathWithRedirectUri('/login', next) : '/login',
        {state: {phone, accountExists: true}},
      );
      return;
    }
    onFormQueryError(r, form as any);
  };

  if (!creds) {
    return (
      <Form
        form={credentialsForm}
        onSubmit={values => {
          start.mutate(values, {
            onSuccess: () => setCreds(values),
            onError: r => handleError(r, credentialsForm, values.phone),
          });
        }}
      >
        <FormPhoneField
          className="mb-20"
          name="phone"
          type="tel"
          label={<Trans message="Phone" />}
          onlyCountries={environment.ONLY_COUNTRIES?.split(',')}
          excludeCountries={environment.EXCLUDED_COUNTRIES?.split(',')}
          initialCountry={environment.ONLY_COUNTRIES?.split(',')[0]}
          required
        />
        <FormTextField
          className="mb-20"
          name="password"
          type="password"
          autoComplete="new-password"
          label={<Trans message="Password" />}
          required
        />
        <FormTextField
          className="mb-32"
          name="password_confirmation"
          type="password"
          autoComplete="new-password"
          label={<Trans message="Confirm Password" />}
          required
        />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={start.isPending}
        >
          <Trans message="Continue" />
        </Button>
      </Form>
    );
  }

  return (
    <Form
      form={otpForm}
      onSubmit={({code}) => {
        verify.mutate(
          {
            phone: creds.phone,
            password: creds.password,
            password_confirmation: creds.password_confirmation,
            code,
            tracking_parameters: getTrackingParametersFromCookie(),
          },
          {
            onSuccess: response => handleSuccess(response),
            onError: r => handleError(r, otpForm, creds.phone),
          },
        );
      }}
    >
      <div className="mb-32 text-sm">
        <Trans message="Enter the verification code we sent to your phone." />
      </div>
      <OtpCodeField className="mb-32" autoFocus />
      <Button
        className="block w-full"
        type="submit"
        variant="flat"
        color="primary"
        size="md"
        disabled={verify.isPending}
      >
        <Trans message="Create account" />
      </Button>
    </Form>
  );
}
